import React, { useEffect, useState } from "react";
import {
  Layout,
  Input,
  Button,
  Empty,
  Space,
  Typography,
  Table,
  Popconfirm,
  message,
  Tooltip,
  Tabs,
  Dropdown,
  Switch,
  Modal,
  Tag,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  EyeOutlined,
  DeleteOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import "./FreessessionRequests.scss";
import Buttons from "../../Components/Buttons/Buttons";
import {
  DELETE_CATEGORY_BY_ID,
  GET_ALL_CATEGORY,
  GET_CATEGORY_BY_ID,
  GET_FREE_SESSIONS,
  UPDATE_FREE_SESSIONS,
  DELETE_FREE_SESSIONS,
  MARK_FREE_SESSIONS_READ,
} from "../../Utlis/apiPath";
import { deleteApi, getApi, putApi } from "../../Utlis/apiService";
import Loader from "../../Components/Loader/Loader";
import { EditOutlined } from "@ant-design/icons";
import { errorToast, successToast } from "../../Services/ToastHelper";
import { debounce } from "lodash";
import { BackArrowIcon } from "../../Components/Icons/BackArrowIcon";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const FreessessionRequests = () => {
  const [addCategoryForm, setAddCategoryForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [isAscending, setIsAscending] = useState(false);
  const [noDataFound, setNoDataFound] = useState(false);
  const [activeTab, setActiveTab] = useState("new");
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [pendingCheckedValue, setPendingCheckedValue] = useState(false);

  const handleAddCategory = () => {
    setEditData(null);
    setAddCategoryForm(true);
  };

  const debouncedFetchClasses = debounce((searchValue) => {
    setSearch(searchValue);
    const page = 1;
    setPageIndex(1);
    fetchAllFreeSessions(page, pageSize, searchValue, activeTab);
  }, 500);

  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    setPageIndex(current);
    setPageSize(pageSize);
    fetchAllFreeSessions(current, pageSize, search, activeTab);
  };

  const getPageSizeOptions = () => {
    if (totalRecords <= 50) {
      return ["10", "20", "50"];
    } else {
      return ["15", "25", "50", "100"];
    }
  };

  const fetchAllFreeSessions = async (
    page = null,
    pageSizeParam = null,
    searchValue = "",
    tab = "new"
  ) => {
    setIsLoading(true);
    try {
      const pageIndex1 = page ? page - 1 : pageIndex - 1;
      const search1 = searchValue || searchValue === "" ? searchValue : search;
      const isActiveValue = tab === "new" ? true : false; // true for new, false for completed
      
      const params = {
        search: search1,
        pageIndex: pageIndex1,
        pageSize: pageSizeParam || pageSize,
        isAscending,
        isActive: isActiveValue, // Filter based on tab
      };
      
      console.log("Fetching with params:", params); // Debug log
      
      const { status, data } = await getApi(GET_FREE_SESSIONS, {
        params,
      });
      
      console.log("API Response:", data); // Debug log
      
      if (status === 200) {
        // Additional client-side filtering as backup
        const filteredRequests = (data?.requests || []).filter(
          (request) => {
            // For "new" tab, show only isActive: true
            // For "completed" tab, show only isActive: false
            if (tab === "new") {
              return request.isActive === true || request.isActive === undefined;
            } else {
              return request.isActive === false;
            }
          }
        );
        
        console.log(`Filtered requests for ${tab} tab:`, filteredRequests); // Debug log

        // The API doesn't reliably populate createdAt on the request itself, so fall
        // back to the auto-increment id (higher id = created later) to order by recency.
        const getSortValue = (request) => {
          const parsed = request.createdAt ? new Date(request.createdAt).getTime() : NaN;
          return !Number.isNaN(parsed) && parsed > 0 ? parsed : Number(request.id) || 0;
        };

        // Show the latest requests first by default; flip when the user toggles sort order
        const sortedRequests = [...filteredRequests].sort((a, b) => {
          const diff = getSortValue(b) - getSortValue(a);
          return isAscending === true ? -diff : diff;
        });

        setRequests(sortedRequests);
        setTotalRecords(data?.totalRecords || 0);
        setNoDataFound(sortedRequests.length === 0 && search1 !== "");

        // Only mark as read if the sidebar badge was showing (count > 0)
        const unreadCount = parseInt(localStorage.getItem("freeSessionCount") || "0", 10);
        if (tab === "new" && filteredRequests.length > 0 && unreadCount > 0) {
          const ids = filteredRequests.map((r) => r.id).filter(Boolean);
          if (ids.length > 0) {
            putApi(MARK_FREE_SESSIONS_READ, { ids }).then(() => {
              localStorage.setItem("freeSessionCount", "0");
            });
          }
        }
      } else {
        setRequests([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching free sessions:", error);
      setRequests([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getCategoryById = async (record) => {
    try {
      const { status, message, data } = await getApi(
        `${GET_CATEGORY_BY_ID}?id=${record.id}`
      );
      if (status === 200) {
        setEditData(data);
        setAddCategoryForm(true);
      } else {
        errorToast(message);
      }
    } catch (error) {
      console.error(error);
      errorToast("An error occurred while deleting the category.");
    }
  };

  useEffect(() => {
    fetchAllFreeSessions(1, 10, search, activeTab);
  }, [isAscending, activeTab]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPageIndex(1);
    setSearch("");
  };

  // View Details
  const showViewDetails = (record) => {
    setSelectedRequest(record);
    setIsViewModalVisible(true);
  };

  const handleViewModalClose = () => {
    setIsViewModalVisible(false);
    setSelectedRequest(null);
  };

  // Delete Free Session
 const handleDelete = async (id) => {
  setIsLoading(true);
  try {
    const response = await deleteApi(`${DELETE_FREE_SESSIONS}/${id}`);
    if (response.status === 200) {
      message.success("Free session request deleted successfully");
      fetchAllFreeSessions(pageIndex, pageSize, search, activeTab);
    } else {
      message.error("Failed to delete free session request");
    }
  } catch (error) {
    console.error("Error deleting free session:", error);
    message.error("Error deleting free session request");
  } finally {
    setIsLoading(false);
  }
};


  // Toggle Completion Status
  const handleToggleStatus = async (record, checked) => {
    setIsLoading(true);
    try {
      const updatedData = {
        isActive: !checked, // Invert: when switch is ON (checked=true), set isActive=false
        action: "markViewed"
      };
      const response = await putApi(
        `${UPDATE_FREE_SESSIONS}?id=${record.id}`,
        updatedData
      );
      if (response.status === 200) {
        message.success(
          `Free session ${checked ? "completed" : "marked as active"} successfully`
        );
        fetchAllFreeSessions(pageIndex, pageSize, search, activeTab);
      } else {
        message.error("Failed to update free session status");
      }
    } catch (error) {
      console.error("Error updating free session status:", error);
      message.error("Error updating free session status");
    } finally {
      setIsLoading(false);
    }
  };

  // New handler for toggle click
const handleToggleClick = (record, checked) => {
  setSelectedRecord(record);
  setPendingCheckedValue(checked);
  setIsModalVisible(true);
};

// Confirm handler
const handleConfirmToggle = () => {
  handleToggleStatus(selectedRecord, pendingCheckedValue);
  setIsModalVisible(false);
  setSelectedRecord(null);
};

// Cancel handler
const handleCancelToggle = () => {
  setIsModalVisible(false);
  setSelectedRecord(null);
};

  // Get menu items for dropdown
  const getMenuItems = (record) => [
    // {
    //   key: "view",
    //   icon: <EyeOutlined />,
    //   label: "View Details",
    //   onClick: () => showViewDetails(record),
    // },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: (
        <Popconfirm
          title="Are you sure you want to delete this request?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
          placement="left"
        >
          <span>Delete</span>
        </Popconfirm>
      ),
      danger: true,
    },
    {
      key: "toggle",
      label: (
        <div onClick={(e) => e.stopPropagation()}>
          <Space>
            <span>Mark as Completed</span>
            <Switch
              checked={!record.isActive} // Invert: isActive=true shows OFF, isActive=false shows ON
              onChange={(checked) => handleToggleClick(record, checked)}
              size="small"
            />
          </Space>
        </div>
      ),
    },
  ];

  const toTitleCase = (text) =>
    (text || "")
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  // The request record's own createdAt is currently always null from the API,
  // so fall back to the user's account createdAt — the only reliable timestamp available.
  const getRequestDate = (record) =>
    record.createdAt || record.updatedAt || record.userId?.createdAt || null;

  // isRead flips to true almost immediately (a bulk mark-as-read fires right after
  // the first fetch), so it can't be used to flag "New" — use recency instead.
  const NEW_REQUEST_WINDOW_MS = 24 * 60 * 60 * 1000;
  const isRecentRequest = (record) => {
    const date = getRequestDate(record);
    return Boolean(date) && Date.now() - new Date(date).getTime() < NEW_REQUEST_WINDOW_MS;
  };

  const newSessionColumns = [
    {
      title: "Name",
      dataIndex: "userId",
      key: "name",
      render: (user, record) => (
        <Space size={6}>
          <span>{toTitleCase(`${user?.firstName || ""} ${user?.lastName || ""}`)}</span>
          {isRecentRequest(record) && (
            <Tag color="green" style={{ marginRight: 0, fontWeight: 600 }}>
              New
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Date",
      key: "date",
      render: (_, record) => {
        const date = getRequestDate(record);
        return date
          ? new Date(date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "—";
      },
    },
    {
      title: "Email",
      dataIndex: ["userId", "email"],
      key: "email",
    },
    {
      title: "Phone Number",
      dataIndex: "userId",
      key: "phoneNumber",
      render: (user) => `${"+"}${user?.phoneNumber || ""}`,
    },
    {
      title: "Gender",
      dataIndex: ["userId", "gender"],
      key: "gender",
      render: (gender) => toTitleCase(gender),
    },
    {
      title: "Health Condition",
      dataIndex: "healthCondition",
      key: "healthCondition",
      render: (text) => {
        const displayText = toTitleCase(text);
        return displayText && displayText.length > 40 ? (
          <Tooltip title={displayText}>{displayText.slice(0, 40)}...</Tooltip>
        ) : (
          displayText || "N/A"
        );
      },
    },
    {
      title: "Focus",
      dataIndex: "focus",
      key: "focus",
      render: (text) =>
        text && text.length > 40 ? (
          <Tooltip title={text}>{text.slice(0, 40)}...</Tooltip>
        ) : (
          text || "N/A"
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getMenuItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            icon={<MoreOutlined />}
            style={{
              border: "2px solid #1890ff",
              borderRadius: "4px",
            }}
          />
        </Dropdown>
      ),
    },
  ];

  const completedSessionColumns = [
    {
      title: "Name",
      dataIndex: "userId",
      key: "name",
      render: (user) => toTitleCase(`${user?.firstName || ""} ${user?.lastName || ""}`),
    },
    {
      title: "Email",
      dataIndex: ["userId", "email"],
      key: "email",
    },
    {
      title: "Phone Number",
      dataIndex: "userId",
      key: "phoneNumber",
      render: (user) => `${"+"}${user?.phoneNumber || ""}`,
    },
    {
      title: "Gender",
      dataIndex: ["userId", "gender"],
      key: "gender",
      render: (gender) => toTitleCase(gender),
    },
    {
      title: "Health Condition",
      dataIndex: "healthCondition",
      key: "healthCondition",
      render: (text) => {
        const displayText = toTitleCase(text);
        return displayText && displayText.length > 40 ? (
          <Tooltip title={displayText}>{displayText.slice(0, 40)}...</Tooltip>
        ) : (
          displayText || "N/A"
        );
      },
    },
    {
      title: "Focus",
      dataIndex: "focus",
      key: "focus",
      render: (text) =>
        text && text.length > 40 ? (
          <Tooltip title={text}>{text.slice(0, 40)}...</Tooltip>
        ) : (
          text || "N/A"
        ),
    },
    {
      title: "Completed Date",
      key: "completedDate",
      render: (_, record) => {
        const date = getRequestDate(record);
        return date
          ? new Date(date).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "—";
      },
    },
  ];

  const handleSorting = () => {
    setIsAscending((prevState) => !prevState);
  };

  const handleEdit = (record) => {
    setAddCategoryForm(true);
  };

  return (
    <div>
      {isLoading && <Loader />}
      {!addCategoryForm && (
        <div className="container2">
          <div className="headers">
            <Space
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Title level={3} style={{ margin: 0 }}>
                Free Session Requests
              </Title>
            </Space>
          </div>

          <Tabs activeKey={activeTab} onChange={handleTabChange}>
            <TabPane tab="New Free Sessions" key="new">
              <div>
                <div className="contentWrapper1">
                  <div className="contentWrapper1_main1" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, width: "100%", borderBottom: "1px solid #e8e8e8", paddingBottom: 12, marginBottom: 4 }}>
                    <Input
                      placeholder="Search"
                      prefix={<SearchOutlined />}
                      className="searchInput"
                      onChange={(e) => debouncedFetchClasses(e.target.value)}
                      value={search}
                      style={{ width: 240, height: 32, margin: 0 }}
                    />
                    <Button
                      icon={
                        isAscending === true ? (
                          <SortAscendingOutlined />
                        ) : (
                          <SortDescendingOutlined />
                        )
                      }
                      className="sortButton"
                      onClick={handleSorting}
                      style={{ height: 32, display: "flex", alignItems: "center", margin: 0 }}
                    />
                  </div>

                  {requests.length > 0 ? (
                    <div className="table-data">
                      <Table
                        columns={newSessionColumns}
                        dataSource={requests}
                        rowKey="id"
                        rowClassName={(record) =>
                          isRecentRequest(record) ? "free-session-row-new" : ""
                        }
                        pagination={{
                          current: pageIndex,
                          pageSize,
                          total: totalRecords,
                          showSizeChanger: true,
                          pageSizeOptions: getPageSizeOptions(),
                        }}
                        onChange={handleTableChange}
                        loading={isLoading}
                      />
                    </div>
                  ) : (
                    <Empty
                      className="emptydata"
                      description={
                        <div>
                          <Title level={4}>No Data Found</Title>
                          <Text>
                            Please try searching with a different keyword.
                          </Text>
                        </div>
                      }
                      imageStyle={{
                        height: 60,
                      }}
                    />
                  )}
                </div>
              </div>
            </TabPane>

            <TabPane tab="Completed Free Sessions" key="completed">
              <div>
                <div className="contentWrapper">
                  <div className="contentWrapper_main" style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, width: "100%", borderBottom: "1px solid #e8e8e8", paddingBottom: 12, marginBottom: 4 }}>
                    <Input
                      placeholder="Search"
                      prefix={<SearchOutlined />}
                      className="searchInput"
                      onChange={(e) => debouncedFetchClasses(e.target.value)}
                      value={search}
                      style={{ width: 240, height: 32, margin: 0 }}
                    />
                    <Button
                      icon={
                        isAscending === true ? (
                          <SortAscendingOutlined />
                        ) : (
                          <SortDescendingOutlined />
                        )
                      }
                      className="sortButton"
                      onClick={handleSorting}
                      style={{ height: 32, display: "flex", alignItems: "center", margin: 0 }}
                    />
                  </div>

                  {requests.length > 0 ? (
                    <div className="table-data">
                      <Table
                        columns={completedSessionColumns}
                        dataSource={requests}
                        rowKey="id"
                        pagination={{
                          current: pageIndex,
                          pageSize,
                          total: totalRecords,
                          showSizeChanger: true,
                          pageSizeOptions: getPageSizeOptions(),
                        }}
                        onChange={handleTableChange}
                        loading={isLoading}
                      />
                    </div>
                  ) : (
                    <Empty
                      className="emptydata"
                      description={
                        <div>
                          <Title level={4}>No Data Found</Title>
                          <Text>
                            Please try searching with a different keyword.
                          </Text>
                        </div>
                      }
                      imageStyle={{
                        height: 60,
                      }}
                    />
                  )}
                </div>
              </div>
            </TabPane>
          </Tabs>
        </div>
      )}
      {/* Confirmation Modal */}
      <Modal
        title="Confirm Status Change"
        open={isModalVisible}
        onCancel={handleCancelToggle}
        footer={[
          <Button key="cancel" onClick={handleCancelToggle}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmToggle}>
            Confirm
          </Button>,
        ]}
        width={400}
      >
        <p>Are you sure you want to change the status to {pendingCheckedValue ? 'completed' : 'active'}?</p>
      </Modal>
    </div>
  );
};

export default FreessessionRequests;